import { computed, ref, watch } from 'vue'
import type { PublishedAgreement } from '@/api/generated-organizations'
import { useOrganizationStore } from '@/stores/organization'
import { usePermissions } from '@/composables/usePermissions'
import { isAgreementAcceptanceInvalid } from '@/composables/useOrganizationOnboarding'
import {
  dismissAgreementPrompt,
  forgetAgreementPromptDismissal,
  isAgreementPromptDismissed
} from '@/services/agreementPromptDismissal'
import { initApiService } from '@/services/init'
import { Permission } from '@/utils/permissionBitmask'
import { getGlobalT } from '@/i18n'
import { extractApiErrorMessage, logUnexpectedFailure } from '@/utils/errorHandling'

/** Bumped by {@link reopenAgreementPrompt}; the mounted prompt answers each bump. */
const reopenRequests = ref(0)

/**
 * Ask the question again in this session, after *Later*: for a screen whose feature waits on an
 * acceptance (Integraties → API-koppelingen) and offers it from there.
 */
export function reopenAgreementPrompt(): void {
  reopenRequests.value++
}

/**
 * Asks a user who may change the practice's settings to accept the current version of each
 * agreement the practice is not yet bound by (#4494): a newly published edition, which does not
 * bind a practice until it accepts it (art. 11.3), or — for a practice that signed itself up
 * before signup asked (#4437) — the first one.
 *
 * Three properties are load-bearing:
 *
 * - **It never blocks work.** *Later* puts the question off until the next sign-in
 *   (`agreementPromptDismissal`), and every failure — the published editions not loading, the
 *   acceptance not being recorded — leaves the practice working exactly as before. Not being
 *   asked is the failure mode that costs nothing.
 * - **Only holders of `organization.settings.update` are asked.** Accepting is on the practice's
 *   behalf, and the backend refuses it from anyone else; a behandelaar is never shown a question
 *   they cannot answer, and nothing is fetched for them.
 * - **What is outstanding is the backend's answer, never worked out here.**
 *   `OrganizationResponse.agreements` says which documents are outstanding and at which version,
 *   and `GET /organizations/agreements` supplies the link and the line saying what changed. A
 *   document the two disagree on — a version published between the two reads — is not shown
 *   until they agree again, rather than shown with another edition's text.
 */
export function useAgreementReacceptance() {
  const t = getGlobalT()
  const organizationStore = useOrganizationStore()
  const { hasPermission } = usePermissions()

  const published = ref<PublishedAgreement[]>([])
  const dismissed = ref(false)
  const accepting = ref(false)
  const error = ref<string | null>(null)

  const canAccept = computed(() => hasPermission(Permission.ORGANIZATION_SETTINGS_UPDATE))

  const outstandingStatuses = computed(() =>
    (organizationStore.organization?.agreements ?? []).filter((status) => status.currentVersionOutstanding)
  )

  /** Each outstanding document at the version it is outstanding at, as the prompt shows it. */
  const outstanding = computed<PublishedAgreement[]>(() =>
    outstandingStatuses.value.flatMap((status) =>
      published.value.filter(
        (agreement) => agreement.document === status.document && agreement.version === status.currentVersion
      )
    )
  )

  /**
   * The question being asked, as one string: the organisation and each outstanding document at
   * its version. A dismissal holds for exactly this and nothing newer.
   */
  const question = computed<string | null>(() => {
    const organizationId = organizationStore.organization?.id
    if (!organizationId || outstandingStatuses.value.length === 0) return null
    return [
      organizationId,
      ...outstandingStatuses.value.map((status) => `${status.document}@${status.currentVersion}`)
    ].join('|')
  })

  const isOpen = computed(() => canAccept.value && !dismissed.value && outstanding.value.length > 0)

  async function loadPublished() {
    try {
      const response = await initApiService().organizations.getPublishedAgreements()
      published.value = response.data
    } catch (err: unknown) {
      // Nothing to show means nothing is asked — the next sign-in asks again
      logUnexpectedFailure('Failed to load the published agreements:', err)
    }
  }

  watch(
    [question, canAccept] as const,
    async ([current, allowed]) => {
      dismissed.value = current !== null && isAgreementPromptDismissed(current)
      if (current === null || !allowed || dismissed.value) return
      await loadPublished()
    },
    { immediate: true }
  )

  watch(reopenRequests, async () => {
    forgetAgreementPromptDismissal()
    dismissed.value = false
    if (question.value !== null && canAccept.value) await loadPublished()
  })

  async function accept() {
    accepting.value = true
    error.value = null
    try {
      const response = await initApiService().organizations.acceptAgreements({
        acceptedAgreements: outstanding.value.map(({ document, version }) => ({ document, version }))
      })
      organizationStore.setAgreements(response.data)
    } catch (err: unknown) {
      if (isAgreementAcceptanceInvalid(err)) {
        // A newer edition was published while the prompt was open: ask about that one instead
        error.value = t('organization.agreementPrompt.outdated')
        await Promise.all([loadPublished(), organizationStore.fetchOrganization().catch(() => undefined)])
      } else {
        error.value = extractApiErrorMessage(err, t('organization.agreementPrompt.acceptFailed'))
        logUnexpectedFailure('Failed to accept the agreements:', err)
      }
    } finally {
      accepting.value = false
    }
  }

  function dismiss() {
    if (question.value !== null) dismissAgreementPrompt(question.value)
    dismissed.value = true
  }

  return {
    isOpen,
    outstanding,
    accepting,
    error,
    accept,
    dismiss
  }
}
